import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import { useAuthStore } from '@/stores/auth'
import type {
	CreditCardEverplansPayment,
	EFT,
	EftEverplansPayment,
} from '@/types'

export const useEverplansResubmissionService = () => {
	const getAfpId = () => {
		const authStore = useAuthStore()
		return authStore?.user?.salesContact?.salesforceId ?? ''
	}
	const updateEverplansEmail = async (planId: string, newEmail: string) => {
		const payload = {
			salesforceContactAfpId: getAfpId(),
			planId,
			newEmail,
		}
		try {
			await purchaseApiAxiosInstance.post('v1/everplans/email-update', payload)
		} catch (e) {
			console.error(
				e,
				`Could not update everplans email for ${planId} at this time`
			)
			throw e
		}
	}

	type SubscriptionUpgradePayload = {
		salesforceContactAfpId: string
		planId: string
		everplansAmount: number
		stripeToken?: string
		eftPayment?: Omit<EFT, 'accountNumber_confirm' | 'routingNumber_confirm'>
		newEmail?: string
	}

	function mapSubscriptionUpgrade(
		planId: string,
		paymentDetails: EftEverplansPayment | CreditCardEverplansPayment,
		newEmail: string | undefined
	): SubscriptionUpgradePayload {
		const payload: SubscriptionUpgradePayload = {
			salesforceContactAfpId: getAfpId(),
			planId,
			everplansAmount: paymentDetails.amount,
			newEmail: newEmail ?? undefined,
		}

		switch (paymentDetails.paymentType) {
			case PlanEverplansPaymentType.EFT:
				payload.eftPayment = {
					accountHolder: paymentDetails.paymentDetails.accountHolder,
					bankName: paymentDetails.paymentDetails.bankName,
					routingNumber: paymentDetails.paymentDetails.routingNumber,
					accountNumber: paymentDetails.paymentDetails.accountNumber,
					accountType: paymentDetails.paymentDetails.accountType,
				}
				break
			case PlanEverplansPaymentType.CreditCard:
				payload.stripeToken = paymentDetails.paymentDetails.token
				break
		}

		if (newEmail) {
			payload.newEmail = newEmail
		}

		return payload
	}

	const upgradeEverplansSubscription = async (
		planId: string,
		paymentDetails: EftEverplansPayment | CreditCardEverplansPayment,
		newEmail?: string
	) => {
		const payload = mapSubscriptionUpgrade(planId, paymentDetails, newEmail)

		try {
			await purchaseApiAxiosInstance.post(
				'v1/everplans/subscription-upgrade',
				payload
			)
		} catch (e) {
			console.error(
				e,
				`Could not update everplans subscription for ${planId} at this time`
			)
			throw e
		}
	}

	return {
		updateEverplansEmail,
		upgradeEverplansSubscription,
	}
}
